import React, { useState } from 'react';
import _ from 'lodash';
import {
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
} from '@mui/material';

import { useTranslation } from 'react-i18next';

import './OpenShiftPicker.scss';
import OpenShiftPickerTable from './OpenShiftPicker.Table';

/** 
 * @param {{
 *  isDisabled: boolean;
 *  onConfirm: (selectedOpenShifts: number[]) => void;
 *  openShiftOptions: Date | string;
 *  period: Date | string;
 *  selectedOpenShiftsOptions: number[];
 * }} param0
 */
const OpenShiftPicker = ({
    isDisabled,
    onConfirm,
    openShiftOptions,
    selectedOpenShiftsOptions,
    period,
}) => {
    const { t } = useTranslation('common');
    const [isShowModal, setIsShowModal] = useState(false);
    const [selectedOpenShifts, setSelectedOpenShifts] = useState(selectedOpenShiftsOptions);

    const handleCancel = () => {
        setIsShowModal(false);
        setSelectedOpenShifts(selectedOpenShiftsOptions);
    }

    const handleConfirm = () => {
        onConfirm(selectedOpenShifts);
        setIsShowModal(false);
    }

    const getErrorMessage = () => {
        if (isDisabled) {
            return null;
        }

        if (!period) {
            return t('openShiftPicker.error.requiredPeriod');
        }

        if (!selectedOpenShifts.length) {
            return t('openShiftPicker.error.requiredLocation');
        }
    }

    const onChange = () => {
        if (!period || isDisabled) {
            return null;
        }

        return setIsShowModal(true);
    }

    /** @param {number} selectedOpenShiftId */
    const toggleSelection = (openShiftId) => {
        const foundIndex = selectedOpenShifts.findIndex((o) => o === openShiftId);

        if (foundIndex !== -1) {
            selectedOpenShifts.splice(foundIndex, 1);
            setSelectedOpenShifts([...selectedOpenShifts]);
            return;
        }

        setSelectedOpenShifts([...selectedOpenShifts, openShiftId]);
    };
    
    /**
     * @param {boolean} checked
     * @param {number[]} value
     */
    const onCheckMany = (checked, value) => {
        if (checked) {
            const updatedSelectedOpenShiftIds = [...selectedOpenShifts, ...value];
            setSelectedOpenShifts(_.uniq(updatedSelectedOpenShiftIds));
        }else {
            const updatedSelectedOpenShiftIds = selectedOpenShifts.filter(x => !value.includes(x));
            setSelectedOpenShifts(updatedSelectedOpenShiftIds);
        }
    };

    return (
        <>
            <TextField
                className={"os-picker-text-field"}
                label={t('openShiftPicker.formLabel')}
                onClick={onChange}
                disabled={!period || isDisabled}
                value={t('openShiftPicker.nbSelectedLabel', { selectedLength: selectedOpenShifts.length })}
                fullWidth
                variant="outlined"
                error={!!(getErrorMessage())}
                helperText={getErrorMessage()}
            />
            <Dialog
                open={isShowModal}
                onClose={handleCancel}
                className='os-picker-modal'
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle
                    id="os-picker-header"
                    className='os-picker-header'
                >
                    <span className='os-picker-title'>
                        {t('openShiftPicker.title')}
                    </span>
                </DialogTitle>
                <DialogContent>
                    <div className='os-picker-content'>
                        <div className='os-picker-selection'>
                            <OpenShiftPickerTable
                                openShiftOptions={openShiftOptions}
                                selectedOptions={selectedOpenShifts}
                                onSelect={toggleSelection}
                                onCheckAll={onCheckMany}
                            />
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        key={'confirm'}
                        color="primary"
                        variant="contained"
                        onClick={handleConfirm}
                    >
                        {t('openShiftPicker.confirm')}
                    </Button>
                    <Button
                        key={'cancel'}
                        color="secondary"
                        variant="contained"
                        onClick={handleCancel}
                    >
                        {t('openShiftPicker.cancel')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default OpenShiftPicker;
